import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../sidebar';
import { useRbac } from '../../context';
import { isAiFunnelOnlyUser } from '../../utils/aiFunnelUser';
import { toast } from 'react-toastify';
import { getPendingComplaintsCount } from '../../services/complaintsApi';
import { getCurrentUser } from '../../services/apiclient';
import { useActiveUserTracking } from '../../hooks/useActiveUser';
import NotificationBell from '../NotificationBell';
import AdminNotificationPopup from '../AdminNotificationPopup';
import ProfileCompletionPopup from '../ProfileCompletionPopup';
import TopHeader from '../TopHeader';
import { cn } from '../lib/utils';

 import {
  LayoutDashboard,
  FilePlus,
  Users,
  Building2,
  Eye,
  UserPlus,
  ClipboardList,
  UserSearch,
  Layers,
  Receipt,
  FileText,
  UserCheck,
  TestTube,
  Cog,
  MessageSquareText,
  BarChart3,
  BookOpenCheck,
  Repeat,
  CheckCircle,
  GraduationCap,
  Network,
  Trophy,
  Phone,
  Award,
  TrendingUp,
  Sparkles,
  Ban,
} from "lucide-react";


interface DashboardLayoutProps {
  children: React.ReactNode;
}

/** Routes where the page manages its own table scroll — layout must not scroll */
const INTERNAL_SCROLL_PATHS = ['/approchesview', '/approchesview2', '/view-applications', '/view-candidates', '/view-joinedcandidates', '/payout-candidates', '/report', '/coordinations'];

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const location = useLocation();
  const usesInternalScroll = INTERNAL_SCROLL_PATHS.includes(location.pathname.toLowerCase());
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // State to control sidebar visibility on mobile
  const [windowWidth, setWindowWidth] = useState<number | null>(null); // State to store window width
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [pendingComplaintsCount, setPendingComplaintsCount] = useState<number>(0);
  const { hasPermission, user, setUser } = useRbac();

  // Initialize active user tracking
  useActiveUserTracking();

  // Refresh session so ADMIN-toggled canPostJobs is visible without re-login
  useEffect(() => {
    if (
      user?.role !== 'SHM' &&
      user?.role !== 'HM' &&
      user?.role !== 'SHM_ADMIN' &&
      user?.role !== 'FBDE_ADMIN'
    ) {
      return;
    }
    let cancelled = false;
    getCurrentUser()
      .then((res: any) => {
        const userData = res?.data?.user ?? res?.user;
        if (!cancelled && userData) {
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.role, setUser]);

  // Use isOnboarded directly from user object
  const hasCompletedOnboarding =
    user?.isOnboarded ||
    isAiFunnelOnlyUser(user) ||
    user?.role === 'ADMIN' ||
    user?.role === 'FBDE' ||
    user?.role === 'CONSULTANT' ||
    user?.role === 'MODERATOR' ||
    user?.role === 'BDE' ||
    user?.role === 'FREELANCE_COUNSELLOR' ||
    user?.role === 'INVESTOR';

  const teamManagementRoles = ['AGENCY', 'AGENCY_ADMIN', 'HM', 'HR', 'SHM', 'SHM_ADMIN', 'FBDE', 'FBDE_ADMIN', 'CONSULTANT', 'ADMIN'];

  // Detect window width on mount and on resize
  useEffect(() => {
    // Set initial window width
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Set initial value
    handleResize();

    // Add resize event listener
    window.addEventListener('resize', handleResize);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Fetch pending complaints count for admins
  useEffect(() => {
    const fetchPendingComplaintsCount = async () => {
      if (hasPermission('manageComplaints')) {
        try {
          const response = await getPendingComplaintsCount();
          if (response.success) {
            setPendingComplaintsCount(response.data.count);
          }
        } catch (error) {
          console.error('Error fetching pending complaints count:', error);
        }
      }
    };

    fetchPendingComplaintsCount();
    
    // Refresh count every 30 seconds
    const interval = setInterval(fetchPendingComplaintsCount, 30000);
    
    return () => clearInterval(interval);
  }, [hasPermission]);

  // If windowWidth is null (e.g., during SSR), don't render yet
  if (windowWidth === null) {
    return null; // Or a loading placeholder if desired
  }

  const isMobile = windowWidth < 800;


const menuItems = [
  {
    icon: <Award size={20} />,
    label: "Recruitment Mahakumb",
    id: "recruitment-mahakumb",
    link: "/recruitment-mahakumb",
    permission: "recruitmentMahakumb",
  },
  {
    icon: <Receipt size={20} />,
    label: "Ai Mode",
    id: "ai-mode",
    link: "/ai-mode",
    permission: "aiMode",
    className: "codepen-button", // Push this item to the bottom
  },
  {
    icon: <LayoutDashboard size={20} />,
    label: "Dashboard",
    id: "dashboard",
    link: "/dashboard",
    permission: "dashboard",
  },
  {
    icon: <TrendingUp size={20} />,
    label: "Investor Dashboard",
    id: "investor-dashboard",
    link: "/investor-dashboard",
    permission: "investorDashboard",
  },
  {
    icon: <Users size={20} />,
    label: "Search Candidates",
    id: "candidate-database",
    link: "/candidate-database",
    permission: "candidateDatabase",
  },
  {
    icon: <FilePlus size={20} />,
    label: "Create New Job",
    id: "create-job",
    link: "/create-job",
    permission: "createJob",
  },
  {
    icon: <ClipboardList size={20} />,
    label: "Create Sub-Job",
    id: "createSubJob",
    link: "/create-subjob",
    permission: "createSubJob",
  },
  {
    icon: <Users size={20} />,
    label: "Users",
    id: "users",
    link: "/users",
    permission: "users",
  },
  {
    icon: <Ban size={20} />,
    label: "Blocked Candidates",
    id: "blocked-candidates",
    link: "/blocked-candidates",
    permission: "manageBlockedCandidates",
  },

  {
    icon: <LayoutDashboard size={20} />,
    label: "Approches",
    id: "approches",
    link: "/ApprochesView",
    permission: "approchesView",
  },
  {
    icon: <Sparkles size={20} />,
    label: "EJ AI response",
    id: "approches-view",
    link: "/ApprochesView2",
    permission: "approchesView",
  },
  {
    icon: <Phone size={20} />,
    label: "Hot Leads",
    id: "hot-leads",
    link: "/hot-leads",
    permission: "hotLeadsView",
  },
  {
    icon: <Phone size={20} />,
    label: "Hotline",
    id: "hotline",
    link: "/hotline",
    permission: "hotline",
  },
  {
    icon: <FileText size={20} />,
    label: "Live sheets",
    id: "live-sheets",
    link: "/admin/live-sheets",
    permission: "liveSheets",
  },
  {
    icon: <Building2 size={20} />,
    label: "Company Approaches",
    id: "company-approaches",
    link: "/company-approaches",
    permission: "companyApproaches",
  },
  {
    icon: <FileText size={20} />,
    label: "Campus Drive Applications",
    id: "campus-drive-applications",
    link: "/campus-drive-applications",
    permission: "campusDriveApplications",
  },

  {
    icon: <Users size={20} />, // Replace Tpo with a valid icon, e.g., Users
    label: "TPO",
    id: "Tpo",
    link: "/Tpos",
    permission: "TpoView",
  },
  {
    icon: <GraduationCap size={20} />,
    label: "College referral Program",
    id: "college-referral-program",
    link: "/college-referral-program",
    permission: "collegeReferralProgram",
  },
  {
    icon: <Building2 size={20} />,
    label: "Company Referral Program",
    id: "company-referral-program",
    link: "/company-referral-program",
    permission: "companyReferralProgram",
  },
  
  {
    icon: <Users size={20} />, // Replace Tpo with a valid icon, e.g., Users
    label: "Public Company Applications",
    id: "publicCompany",
    link: "/publiccompanies/applications",
    permission: "publicCompanyApplications",
  },
  {
    icon: <Building2 size={20} />,
    label: "Companies",
    id: "companies",
    link: "/companies",
    permission: "companies",
  },
  {
    icon: <Eye size={20} />,
    label: "View Interviews",
    id: "view-applications",
    link: "/view-applications",
    permission: "viewApplications",
  },
   {
    icon: <Eye size={20} />,
    label: "Joined Candidates",
    id: "JoinedCandidates",
    link: "/view-joined-Candidates",
    permission: "JoinedCandidates",
  },
    {
    icon: <UserSearch size={20} />,
    label: "View Joined candidates",
    id: "view-joined-applications",
    link: "/view-JoinedCandidates",
    permission: "joinedCandidates",
  },
  {
    icon: <FileText size={20} />,
    label: "Offer/Experience Letter",
    id: "offer-experience-letter",
    link: "/offer-experience-letter",
    permission: "joinedCandidates",
  },
   {
    icon:  <UserSearch size={20} /> ,
    label: "View Candidates",
    id: "view-candidate",
    link: "/view-candidates",
    permission: "viewCandidates",
  },
    {
    icon:  <UserSearch size={20} /> ,
    label: "View Payout Candidates",
    id: "view-payout-candidates",
    link: "/payout-candidates",
    permission: "PayoutCandidates",
  },

   {
    icon:  <UserSearch size={20} /> ,
    label: "View Tenure Candidates",
    id: "view-tenure-candidates",
    link: "/tenure-candidates",
    permission: "TenureCandidates",
  },

  {
    icon: <UserPlus size={20} />,
    label: "Add Candidate",
    id: "add-candidate",
    link: "/add-candidate",
    permission: "addCandidates",
  },
    {
    icon: <UserPlus size={20} />,
    label: "Talent Pool",
    id: "talent-pool",
    link: "/TalentPool",
    permission: "TalentPool",
  },
  {
    icon: <ClipboardList size={20} />,
    label: "My Hiring Managers",
    id: "myHiringManagers",
    link: "/myHiringManagers",
    permission: "myHiringManagers",
  },
  {
    icon: <Layers size={20} />,
    label: "View sub jobs",
    id: "ViewSubJobs",
    link: "/viewSubJobs",
    permission: "ViewSubJobs",
  },
    {
    icon: <Layers size={20} />, // Use a Lucide icon for consistency
    label: "Leader Board",
    id: "leader-board",
    link: "/leaderboard",
    permission: "leaderBoard",
  },
  {
    icon: <ClipboardList size={20} />,
    label: "Assessments",
    id: "assessments",
    link: "/assessments",
    permission: "assessments",

  },
   {
    icon: <FileText size={20} />,
    label: "Assessment Transactions",
    id: "assessmentTransactions",
    link: "/transactionsOfAssessment",
    permission: "transactions",
  },
 {
    icon: <UserCheck size={20} />,
    label: "Assessment Candidates",
    id: "candidatesTransactions",
    link: "/candidatesOfAssessment",
    permission: "candidatesOfAssessment",
  },
  {
    icon: <Cog size={20} />,
    label: "Exam Candidates",
    id: "exam",
    link: "/exam/candidates",
    permission: "exam",
  },
  {
    icon: <Cog size={20} />,
    label: "External Job Applications",
    id: "externalJobApplications",
    link: "/ExternalJobApplications",
    permission: "externalJobApplications",
  },
  
    {
    icon: <Cog size={20} />,
    label: "Public Job Applications",
    id: "publicJobApplications",
    link: "/PublicJobApplications",
    permission: "dashboard",
  },
      {
    icon: <Cog size={20} />,
    label: "Public Subjob Applications",
    id: "publicSubjobApplications",
    link: "/PublicSubjobApplications",
    permission: "publicSubjobApplications",
  },
   {
    icon: <Repeat size={20} />,
    label: "Candidates of the Day",
    id: "assignedJobs",
    link: "/assignedJobs",
    permission: "assignedJobs",
  },
  {
    icon: <MessageSquareText size={20} />,
    label: hasPermission('manageComplaints') 
      ? `Manage Complaints${pendingComplaintsCount > 0 ? ` (${pendingComplaintsCount})` : ''}`
      : "My Complaints",
    id: "complaints",
    link: hasPermission('manageComplaints') ? "/admin/complaints" : "/complaints",
    permission: "manageComplaints",
    showForAll: true, // Custom property to show for all users
  },
  {
    icon: <Users size={20} />,
    label: "Live Active Users",
    id: "active-users",
    link: "/active-users",
    permission: "activeUsers",
  },
  {
    icon: <MessageSquareText size={20} />,
    label: "Communication Support",
    id: "communicationSupport",
    link: "/communicationsupport",
    permission: "tpoFeatures",
  },
  {
    icon: <UserCheck size={20} />,
    label: "Agency onboarding approvals",
    id: "agency-onboarding-approvals",
    link: "/admin/agency-onboarding",
    permission: "dashboard",
  },
  {
    icon: <ClipboardList size={20} />,
    label: "Recruiter Requests",
    id: "recruiter-requests",
    link: "/admin/recruiter-requests",
    permission: "dashboard",
  },
  {
    icon: <MessageSquareText size={20} />,
    label: "Communication Server",
    id: "communication-server",
    link: "/communication-server",
    permission: "dashboard",
  },
  {
    icon: <BookOpenCheck size={20} />,
    label: "Workshops & Resources",
    id: "Workshops&Resources",
    link: "/workshops&resources",
    permission: "tpoFeatures",
  },
  {
    icon: <BarChart3 size={20} />,
    label: "Placement Stats",
    id: "placementStats",
    link: "/placementStats",
    permission: "tpoFeatures",
  },
  {
    icon: <CheckCircle size={20} />,
    label: "ATS Checker",
    id: "ats-checker",
    link: "/ats-checker",
    permission: "atsChecker",
  },
  {
    icon: <Network size={20} />,
    label: "Coordinations",
    id: "coordinations",
    link: "/coordinations",
    permission: "coordinations",
  },
  {
    icon: <Trophy size={20} />,
    label: "Highlights",
    id: "highlights",
    link: "/highlights",
    permission: "highlights",
  },
  {
    icon: <BarChart3 size={20} />,
    label: "Report",
    id: "report",
    link: "/report",
    permission: "dashboard",
  },
  {
    icon: <Phone size={20} />,
    label: "AI Call agent",
    id: "ai-call-agent",
    link: "/ai-call-agent",
    permission: "aiCallAgent",
  },
  {
    icon: <Phone size={20} />,
    label: "AI Funnel",
    id: "hunar-funnel",
    link: "/hunar-funnel",
    permission: "hunarFunnelView",
  },
  {
    icon: <Cog size={20} />,
    label: "AI Allocations",
    id: "hunar-allocations",
    link: "/hunar-allocations",
    permission: "hunarAllocationManage",
  },
  {
    icon: <FileText size={20} />,
    label: "Blogs",
    id: "blogs",
    link: "/admin/blogs",
    permission: "dashboard", // Using dashboard permission, but will check ADMIN role in route
  },
  {
    icon: <Users size={20} />,
    label: "Teams",
    id: "teams",
    link: "/admin/teams",
    permission: "dashboard",
  },
];


// Get all menu items regardless of onboarding status
  const filteredItems = isAiFunnelOnlyUser(user)
    ? menuItems.filter((item: any) => item.id === "hunar-funnel")
    : menuItems.filter((item: any) => {
    // My Hiring Managers / My Recruiters — agency & hiring hierarchy roles
    if (item.id === 'myHiringManagers') {
      return teamManagementRoles.includes(user?.role || '');
    }

    // Show Offer/Experience Letter for creator and viewer roles
    if (item.id === "offer-experience-letter") {
      return ["ADMIN", "SHM", "SHM_ADMIN", "AGENCY_ADMIN", "FBDE_ADMIN", "FBDE", "HM", "AGENCY", "HR"].includes(user?.role || "");
    }

    if (item.id === "dashboard" && user?.role === "INVESTOR") {
      return false;
    }

    // Hide "Public Job Applications" for HR users
    if (item.id === "publicJobApplications" && (user?.role === "HR" || user?.role === "TPO" || user?.role === "BDE_INTERN" || user?.role === "FREELANCE_BDE" || user?.role === "MASTER_BDE" || user?.role === "MODERATOR" || user?.role === "COMPANY")) {
      return false;
    }
    // Show Report for ADMIN + hierarchy managers (scoped to own subtree on the API)
    if (
      item.id === "report" &&
      !["ADMIN", "FBDE", "SHM", "SHM_ADMIN", "FBDE_ADMIN", "HR", "HM"].includes(user?.role || "")
    ) {
      return false;
    }
    // Show AI Call agent only for ADMIN
    if (item.id === "ai-call-agent" && user?.role !== "ADMIN") {
      return false;
    }
    // Show Blogs only for ADMIN
    if (item.id === "blogs" && user?.role !== "ADMIN") {
      return false;
    }
    if (item.id === "teams" && user?.role !== "ADMIN") {
      return false;
    }
    // Show Leads only for ADMIN
    if (item.id === "leads" && user?.role !== "ADMIN") {
      return false;
    }
    // Show Communication Server only for ADMIN
    if (item.id === "communication-server" && user?.role !== "ADMIN") {
      return false;
    }
    if (item.id === "agency-onboarding-approvals" && user?.role !== "ADMIN") {
      return false;
    }
    if (item.id === "recruiter-requests" && user?.role !== "ADMIN") {
      return false;
    }
    // Show complaints tab for all users, but check manageComplaints permission for admin features
    if (item.showForAll) {
      if (item.id === "complaints" && user?.role === "INVESTOR") {
        return false;
      }
      return true;
    }
    
    // Show Create New Job when role has createJob OR ADMIN enabled canPostJobs (SHM/HM)
    if (item.id === "create-job") {
      return hasPermission("createJob") || Boolean(user?.canPostJobs);
    }

    return hasPermission(item.permission);
  });

  // Instead of hiding menu items, we'll pass an isDisabled flag
  const accessibleMenuItems = filteredItems.map(item => ({
    ...item,
    isDisabled:
      item.id === 'myHiringManagers' && teamManagementRoles.includes(user?.role || '')
        ? !user?.isOnboarded
        : !hasCompletedOnboarding,
    // Always provide an onClick handler (no-op if enabled)
    onClick: !hasCompletedOnboarding
      ? (e: React.MouseEvent) => {
          e.preventDefault();
          toast.error("Please complete onboarding to access this feature");
        }
      : (e: React.MouseEvent) => {},
  }));

const toggleSidebar = () => {
  if (isMobile) {
    setIsSidebarOpen((prev) => !prev);
  } else {
    setIsCollapsed((prev) => !prev);
  }
};

// Define closeSidebar function for Sidebar prop
const closeSidebar = () => {
  setIsSidebarOpen(false);
};

// Handle Leader Board click
const handleLeaderBoardClick = (e: React.MouseEvent) => {
  e.preventDefault();
  
  // Dispatch custom event to show coming soon modal with custom message
  const event = new CustomEvent('comingSoonFeature', {
    detail: {
      title: "Leader Board Coming Soon!",
      message: "The Leader Board feature is currently under development. Stay tuned for exciting updates!"
    }
  });
  window.dispatchEvent(event);
};

  return (
    <div className="h-screen min-w-0 bg-[#EDEEF1] flex relative items-stretch" style={{overflow: 'hidden' }}>
      {/* Hamburger Menu (visible on mobile) - always show */}
      {isMobile && (
        <nav className={`fixed top-0 left-0 w-full h-[72px] bg-white z-40 flex items-center justify-between px-4`}>
          <button
            className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-100 transition-all duration-200"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="text-gray-500"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          
          {/* EarlyJobs Logo - Mobile Header */}
          <div className="flex-1 flex justify-center">
            <img 
              src="/assets/images/earlyjobs.png" 
              alt="EarlyJobs" 
              className="h-16 w-auto"
            />
          </div>

          {/* Notification Bell - Mobile Header */}
          {user && (
            <div className="flex items-center">
              <NotificationBell />
            </div>
          )}
        </nav>
      )}

      {/* Sidebar - always show but with conditional menu items */}
      <Sidebar
        menuItems={accessibleMenuItems}
        isSidebarOpen={isSidebarOpen}
        closeSidebar={closeSidebar}
        isMobile={isMobile}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        isDisabled={!hasCompletedOnboarding}
      />

      {/* Sidebar Toggle Button - always show */}
      {!isMobile && (
        <button
          onClick={toggleSidebar}
          style={{
            backgroundColor: "#EC6E51",
            borderRadius: "100%",
            cursor: "pointer",
            zIndex: 45,
            minHeight: "32px",
            minWidth: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: !isCollapsed
              ? "0px solid #d3d3d3"
              : "1px solid #d3d3d3",
          } as React.CSSProperties}
          className={`
            p-1.5 rounded-lg absolute
            ${isCollapsed ? "top-[32px] right-auto left-[72px]" : "top-[54px] right-[calc(100%-18rem)] left-auto"}
            hover:bg-[#F4A693] transition-colors duration-200
          `}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`text-[#fff] transition-transform duration-300 ${isCollapsed ? "rotate-180" : ""
              }`}
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      )}

      {/* Main Content */}
      <main 
        className="flex-1 min-w-0 border border-[#d3d3d3] bg-white overflow-hidden relative flex flex-col min-h-0"
        style={isMobile ? { margin: "0px", borderRadius: "0px", marginTop: "72px", height: "calc(100vh - 72px)" } : { margin: "0.75rem", borderRadius: "16px", height: "calc(100vh - 1.5rem)" }}
      >
        {/* Top Header - Notification, Username, Logout */}
        <TopHeader />
        
        {/* Page Content */}
        <div
          className={cn(
            "flex-1 min-w-0 min-h-0 flex flex-col",
            usesInternalScroll ? "overflow-hidden" : "overflow-auto"
          )}
        >
          {children}
        </div>
      </main>

      {/* Admin Notification Popup - Global component */}
      <AdminNotificationPopup />
      
      {/* Profile Completion Popup - Shows reminder every 2 hours */}
      {!isAiFunnelOnlyUser(user) && <ProfileCompletionPopup />}
    </div>
  );
};

export default DashboardLayout;